/* eslint-disable no-bitwise -- every bit operation on permissions lives in this file, in the helpers below. */

/**
 * Permissions are sets of flags stored in one whole number. A flag is a power of two, from 2^0 up to 2^52, so
 * the number stays exact in JSON, in JavaScript, and in a Postgres `bigint` column.
 *
 * JavaScript's own bit operators only see 32 bits: `a & b` silently drops everything above bit 31. So nothing
 * outside this file may use them on permissions (a lint rule enforces it). Use `hasPermission`, `permOr`,
 * `permAnd`, `permMinus`, `permHasAny` and `permWithin` instead; they are exact at any size.
 */

/** The highest flag a permission can use: 2^52, the last power of two a JavaScript number holds exactly. */
export const MAX_PERMISSION_BIT = 52;

/** The largest value a set of permissions can have: all 53 flags on. */
export const MAX_PERMISSIONS = Number.MAX_SAFE_INTEGER;

const INT_MAX = 0x7fffffff;
const big = (n: number): bigint => BigInt(Number.isSafeInteger(n) && n > 0 ? n : 0);
const small = (a: number, b: number): boolean => a <= INT_MAX && b <= INT_MAX && a >= 0 && b >= 0;

/** Every flag in any of the sets: `a | b | ...`. */
export function permOr(...sets: number[]): number {
  let out = 0n;
  let fast = 0;
  let smallOnly = true;
  for (const s of sets) {
    if (smallOnly && s >= 0 && s <= INT_MAX) fast |= s;
    else {
      if (smallOnly) {
        smallOnly = false;
        out = BigInt(fast);
      }
      out |= big(s);
    }
  }
  return smallOnly ? fast : Number(out);
}

/** The flags in both sets: `a & b`. */
export function permAnd(a: number, b: number): number {
  return small(a, b) ? a & b : Number(big(a) & big(b));
}

/** The flags in `a` that are not in `b`: `a & ~b`. */
export function permMinus(a: number, b: number): number {
  return small(a, b) ? a & ~b : Number(big(a) & ~big(b));
}

/** Whether the two sets share any flag. */
export function permHasAny(a: number, b: number): boolean {
  return permAnd(a, b) !== 0;
}

/** Whether every flag in `a` is also in `of`. */
export function permWithin(a: number, of: number): boolean {
  return permMinus(a, of) === 0;
}

/** Whether `perms` has every flag in `bit`. */
export function hasPermission(perms: number, bit: number): boolean {
  return small(perms, bit) ? (perms & bit) === bit : permAnd(perms, bit) === bit;
}
export const Permission = {
  VIEW_CHANNEL: 1 << 0,
  SEND_MESSAGES: 1 << 1,
  READ_MESSAGE_HISTORY: 1 << 2,
  ATTACH_FILES: 1 << 3,
  EMBED_LINKS: 1 << 4,
  ADD_REACTIONS: 1 << 5,
  MENTION_EVERYONE: 1 << 6,
  MANAGE_MESSAGES: 1 << 7,
  CREATE_THREADS: 1 << 8,
  SEND_MESSAGES_IN_THREADS: 1 << 9,
  CONNECT: 1 << 10,
  SPEAK: 1 << 11,
  STREAM: 1 << 12,
  MUTE_MEMBERS: 1 << 13,
  DEAFEN_MEMBERS: 1 << 14,
  MOVE_MEMBERS: 1 << 15,
  MANAGE_CHANNELS: 1 << 16,
  MANAGE_ROLES: 1 << 17,
  MANAGE_SPACE: 1 << 18,
  KICK_MEMBERS: 1 << 19,
  BAN_MEMBERS: 1 << 20,
  MODERATE_MEMBERS: 1 << 21,
  CREATE_INVITE: 1 << 22,
  VIEW_AUDIT_LOG: 1 << 23,
  CHANGE_NICKNAME: 1 << 24,
  MANAGE_NICKNAMES: 1 << 25,
  ADMINISTRATOR: 1 << 26,
  SCREEN_SHARE: 1 << 27,
  REQUEST_TO_SPEAK: 1 << 28,
  /** Bots only: edit messages they sent. People can always edit their own messages. */
  EDIT_OWN_MESSAGES: 1 << 29,
  /** Bots only: delete messages they sent. People can always delete their own messages. */
  DELETE_OWN_MESSAGES: 1 << 30,
  /** Create, edit, start, end and cancel scheduled events. Everyone can say they are interested. The first flag past 31 bits. */
  MANAGE_EVENTS: 2 ** 31,
  /** Create, edit and delete a space's incoming and outgoing webhooks. */
  MANAGE_WEBHOOKS: 2 ** 32,
} as const;

export type PermissionName = keyof typeof Permission;

export const ALL_PERMISSIONS = permOr(...Object.values(Permission));

export const DEFAULT_EVERYONE_PERMISSIONS = permOr(
  Permission.VIEW_CHANNEL,
  Permission.SEND_MESSAGES,
  Permission.READ_MESSAGE_HISTORY,
  Permission.ATTACH_FILES,
  Permission.EMBED_LINKS,
  Permission.ADD_REACTIONS,
  Permission.CREATE_THREADS,
  Permission.SEND_MESSAGES_IN_THREADS,
  Permission.CONNECT,
  Permission.SPEAK,
  Permission.STREAM,
  Permission.SCREEN_SHARE,
  Permission.REQUEST_TO_SPEAK,
  Permission.CREATE_INVITE,
  Permission.CHANGE_NICKNAME,
);

/** Permissions every participant has inside a direct or group conversation. */
export const PRIVATE_CHANNEL_PERMISSIONS = permOr(
  Permission.VIEW_CHANNEL,
  Permission.SEND_MESSAGES,
  Permission.READ_MESSAGE_HISTORY,
  Permission.ATTACH_FILES,
  Permission.EMBED_LINKS,
  Permission.ADD_REACTIONS,
  Permission.CREATE_THREADS,
  Permission.SEND_MESSAGES_IN_THREADS,
  Permission.CONNECT,
  Permission.SPEAK,
  Permission.STREAM,
  Permission.SCREEN_SHARE,
);

/** Removed while a member is timed out. They can still read. */
export const TIMEOUT_REVOKED = permOr(
  Permission.SEND_MESSAGES,
  Permission.SEND_MESSAGES_IN_THREADS,
  Permission.CREATE_THREADS,
  Permission.ADD_REACTIONS,
  Permission.CONNECT,
  Permission.SPEAK,
  Permission.STREAM,
  Permission.SCREEN_SHARE,
  Permission.REQUEST_TO_SPEAK,
  Permission.CHANGE_NICKNAME,
);

/** Any of these makes someone a stage moderator: they can pick speakers and move people to the audience. */
export const STAGE_MODERATOR_PERMISSIONS = permOr(Permission.MUTE_MEMBERS, Permission.MOVE_MEMBERS, Permission.MANAGE_CHANNELS);

export function isStageModerator(perms: number): boolean {
  return permHasAny(perms, STAGE_MODERATOR_PERMISSIONS);
}

/** Channel members who skip slow mode. */
export const SLOWMODE_BYPASS = permOr(Permission.MANAGE_MESSAGES, Permission.MANAGE_CHANNELS);

/**
 * What people can always do with their own messages. Bots only get these when they are granted, so the
 * resolver adds them for everyone else; that also lets a person grant them to a bot's role.
 */
export const OWN_MESSAGE_PERMISSIONS = permOr(Permission.EDIT_OWN_MESSAGES, Permission.DELETE_OWN_MESSAGES);

/**
 * Everything a bot can ask for when it is added to a space. Administrator is never among them, and nothing
 * here is granted that the person adding the bot does not have.
 */
export const BOT_PERMISSIONS = permOr(
  Permission.VIEW_CHANNEL,
  Permission.READ_MESSAGE_HISTORY,
  Permission.SEND_MESSAGES,
  Permission.SEND_MESSAGES_IN_THREADS,
  Permission.CREATE_THREADS,
  Permission.ATTACH_FILES,
  Permission.EMBED_LINKS,
  Permission.EDIT_OWN_MESSAGES,
  Permission.DELETE_OWN_MESSAGES,
  Permission.MANAGE_MESSAGES,
  Permission.ADD_REACTIONS,
  Permission.MANAGE_CHANNELS,
  Permission.MANAGE_ROLES,
  Permission.KICK_MEMBERS,
  Permission.BAN_MEMBERS,
  Permission.MENTION_EVERYONE,
  Permission.MODERATE_MEMBERS,
  Permission.MANAGE_NICKNAMES,
  Permission.CREATE_INVITE,
  Permission.VIEW_AUDIT_LOG,
  Permission.MANAGE_EVENTS,
  Permission.MANAGE_WEBHOOKS,
);

/** The order bot permissions are listed in, on the consent screen and in the Developer Portal. */
export const BOT_PERMISSION_ORDER: readonly PermissionName[] = [
  'VIEW_CHANNEL',
  'READ_MESSAGE_HISTORY',
  'SEND_MESSAGES',
  'SEND_MESSAGES_IN_THREADS',
  'CREATE_THREADS',
  'ATTACH_FILES',
  'EMBED_LINKS',
  'EDIT_OWN_MESSAGES',
  'DELETE_OWN_MESSAGES',
  'ADD_REACTIONS',
  'MENTION_EVERYONE',
  'MANAGE_MESSAGES',
  'MANAGE_CHANNELS',
  'MANAGE_ROLES',
  'KICK_MEMBERS',
  'BAN_MEMBERS',
  'MODERATE_MEMBERS',
  'MANAGE_NICKNAMES',
  'CREATE_INVITE',
  'VIEW_AUDIT_LOG',
  'MANAGE_EVENTS',
  'MANAGE_WEBHOOKS',
];

/** What a new bot asks for until its owner changes it. */
export const DEFAULT_BOT_PERMISSIONS = permOr(
  Permission.VIEW_CHANNEL,
  Permission.READ_MESSAGE_HISTORY,
  Permission.SEND_MESSAGES,
  Permission.SEND_MESSAGES_IN_THREADS,
  Permission.EDIT_OWN_MESSAGES,
  Permission.DELETE_OWN_MESSAGES,
  Permission.ADD_REACTIONS,
);

/** Permissions that only make sense at the space level and cannot be overwritten per channel. */
export const SPACE_ONLY_PERMISSIONS = permOr(
  Permission.MANAGE_SPACE,
  Permission.KICK_MEMBERS,
  Permission.BAN_MEMBERS,
  Permission.MODERATE_MEMBERS,
  Permission.VIEW_AUDIT_LOG,
  Permission.CHANGE_NICKNAME,
  Permission.MANAGE_NICKNAMES,
  Permission.MANAGE_EVENTS,
  Permission.MANAGE_WEBHOOKS,
  Permission.ADMINISTRATOR,
);

export const PERMISSION_INFO: Record<PermissionName, { label: string; description: string; group: string }> = {
  VIEW_CHANNEL: { label: 'View channels', description: 'See channels and read live messages.', group: 'General' },
  MANAGE_CHANNELS: { label: 'Manage channels', description: 'Create, edit, reorder and delete channels.', group: 'General' },
  MANAGE_ROLES: { label: 'Manage roles', description: 'Create and edit roles below their highest role, and edit channel permissions.', group: 'General' },
  MANAGE_SPACE: { label: 'Manage space', description: 'Change the space name, icon and invites.', group: 'General' },
  MANAGE_WEBHOOKS: { label: 'Manage webhooks', description: 'Create, edit and delete webhooks, which let other services post in channels and hear what happens here.', group: 'General' },
  MANAGE_EVENTS: { label: 'Manage events', description: 'Create, edit, start, end and cancel scheduled events. Everyone can mark themselves interested.', group: 'General' },
  VIEW_AUDIT_LOG: { label: 'View audit log', description: 'Read the record of administrative actions.', group: 'General' },
  CREATE_INVITE: { label: 'Create invites', description: 'Invite new people to the space.', group: 'Membership' },
  CHANGE_NICKNAME: { label: 'Change nickname', description: 'Change their own nickname in this space.', group: 'Membership' },
  MANAGE_NICKNAMES: { label: 'Manage nicknames', description: 'Change the nicknames of other members.', group: 'Membership' },
  KICK_MEMBERS: { label: 'Kick members', description: 'Remove members, who can rejoin with an invite.', group: 'Membership' },
  BAN_MEMBERS: { label: 'Ban members', description: 'Permanently remove members and block them from rejoining.', group: 'Membership' },
  MODERATE_MEMBERS: { label: 'Time out members', description: 'Temporarily stop members from talking, and review reports.', group: 'Membership' },
  SEND_MESSAGES: { label: 'Send messages', description: 'Post messages in text channels.', group: 'Text' },
  SEND_MESSAGES_IN_THREADS: { label: 'Send messages in threads and posts', description: 'Reply in threads and in forum and media posts.', group: 'Text' },
  CREATE_THREADS: { label: 'Create threads and posts', description: 'Start threads from messages, and create posts in forum and media channels.', group: 'Text' },
  EMBED_LINKS: { label: 'Embed links', description: 'Links they post show a preview. Bots also need it to send embeds.', group: 'Text' },
  ATTACH_FILES: { label: 'Attach files', description: 'Upload files and images.', group: 'Text' },
  ADD_REACTIONS: { label: 'Add reactions', description: 'React to messages.', group: 'Text' },
  MENTION_EVERYONE: { label: 'Mention @everyone and all roles', description: 'Notify everyone, or roles that are not mentionable.', group: 'Text' },
  MANAGE_MESSAGES: { label: 'Manage messages', description: 'Delete and pin other members’ messages, and archive threads.', group: 'Text' },
  READ_MESSAGE_HISTORY: { label: 'Read message history', description: 'Read messages sent before they opened the channel.', group: 'Text' },
  CONNECT: { label: 'Connect', description: 'Join voice and stage channels.', group: 'Voice' },
  SPEAK: { label: 'Speak', description: 'Talk in voice channels, and on stage once they are a speaker.', group: 'Voice' },
  STREAM: { label: 'Video', description: 'Turn on their camera.', group: 'Voice' },
  SCREEN_SHARE: { label: 'Screen share', description: 'Share their screen.', group: 'Voice' },
  REQUEST_TO_SPEAK: { label: 'Request to speak', description: 'Raise their hand to ask to become a speaker in stage channels.', group: 'Voice' },
  MUTE_MEMBERS: { label: 'Mute members', description: 'Mute other members in voice.', group: 'Voice' },
  DEAFEN_MEMBERS: { label: 'Deafen members', description: 'Deafen other members in voice.', group: 'Voice' },
  MOVE_MEMBERS: { label: 'Disconnect members', description: 'Disconnect other members from voice.', group: 'Voice' },
  ADMINISTRATOR: { label: 'Administrator', description: 'Every permission, and bypasses channel overrides. Grant with care.', group: 'Advanced' },
  EDIT_OWN_MESSAGES: { label: 'Edit own messages', description: 'Bots only: edit messages they sent. People can always edit their own.', group: 'Bots' },
  DELETE_OWN_MESSAGES: { label: 'Delete own messages', description: 'Bots only: delete messages they sent. People can always delete their own.', group: 'Bots' },
};

export interface RoleLike {
  id: string;
  permissions: number;
  position: number;
}

export interface OverwriteLike {
  targetType: 'role' | 'member';
  targetId: string;
  allow: number;
  deny: number;
}

export interface PermissionSubject {
  userId: string;
  ownerId: string;
  /** The @everyone role id for the space. */
  everyoneRoleId: string;
  roles: readonly RoleLike[];
  memberRoleIds: readonly string[];
  /** ISO timestamp; the member is timed out while this is in the future. */
  timeoutUntil?: string | null;
  /**
   * A bot gets only what its roles grant: not the @everyone role's permissions (so what was agreed when it was
   * added is all it has), and not the own-message permissions people always have. Channel overrides still apply.
   */
  bot?: boolean;
  now?: number;
}

export function isTimedOut(subject: Pick<PermissionSubject, 'timeoutUntil' | 'now'>): boolean {
  if (!subject.timeoutUntil) return false;
  return new Date(subject.timeoutUntil).getTime() > (subject.now ?? Date.now());
}

export function computeBasePermissions(subject: PermissionSubject): number {
  if (subject.userId === subject.ownerId) return ALL_PERMISSIONS;
  const roleMap = new Map(subject.roles.map((r) => [r.id, r]));
  let perms = subject.bot ? 0 : permOr(roleMap.get(subject.everyoneRoleId)?.permissions ?? 0, OWN_MESSAGE_PERMISSIONS);
  for (const roleId of subject.memberRoleIds) {
    if (roleId === subject.everyoneRoleId) continue;
    const role = roleMap.get(roleId);
    if (role) perms = permOr(perms, role.permissions);
  }
  if (hasPermission(perms, Permission.ADMINISTRATOR)) return ALL_PERMISSIONS;
  if (isTimedOut(subject)) perms = permMinus(perms, TIMEOUT_REVOKED);
  return perms;
}

function applyOverwrites(perms: number, subject: PermissionSubject, overwrites: readonly OverwriteLike[]): number {
  if (overwrites.length === 0) return perms;
  const everyone = overwrites.find((o) => o.targetType === 'role' && o.targetId === subject.everyoneRoleId);
  if (everyone) perms = permOr(permMinus(perms, everyone.deny), everyone.allow);

  let allow = 0;
  let deny = 0;
  const memberRoles = new Set(subject.memberRoleIds);
  for (const o of overwrites) {
    if (o.targetType === 'role' && o.targetId !== subject.everyoneRoleId && memberRoles.has(o.targetId)) {
      allow = permOr(allow, o.allow);
      deny = permOr(deny, o.deny);
    }
  }
  perms = permOr(permMinus(perms, deny), allow);

  const member = overwrites.find((o) => o.targetType === 'member' && o.targetId === subject.userId);
  if (member) perms = permOr(permMinus(perms, member.deny), member.allow);
  return perms;
}

/**
 * Effective permissions in a channel. Category overwrites apply first, then
 * the channel's own overwrites, so a channel can re-open what its category hides.
 */
export function computeChannelPermissions(
  subject: PermissionSubject,
  categoryOverwrites: readonly OverwriteLike[],
  channelOverwrites: readonly OverwriteLike[],
): number {
  const base = computeBasePermissions(subject);
  if (hasPermission(base, Permission.ADMINISTRATOR)) return ALL_PERMISSIONS;

  let perms = applyOverwrites(base, subject, categoryOverwrites);
  perms = applyOverwrites(perms, subject, channelOverwrites);
  // Space-level permissions are not affected by channel overwrites.
  perms = permOr(permMinus(perms, SPACE_ONLY_PERMISSIONS), permAnd(base, SPACE_ONLY_PERMISSIONS));

  if (isTimedOut(subject)) perms = permMinus(perms, TIMEOUT_REVOKED);
  if (!hasPermission(perms, Permission.VIEW_CHANNEL)) return 0;
  // Overrides can't take these from people, who can always edit and delete their own messages.
  if (!subject.bot) perms = permOr(perms, OWN_MESSAGE_PERMISSIONS);
  if (!hasPermission(perms, Permission.SEND_MESSAGES)) {
    perms = permMinus(perms, permOr(Permission.MENTION_EVERYONE, Permission.ATTACH_FILES, Permission.EMBED_LINKS));
  }
  if (!hasPermission(perms, Permission.CONNECT)) {
    perms = permMinus(perms, permOr(Permission.SPEAK, Permission.STREAM, Permission.SCREEN_SHARE, Permission.REQUEST_TO_SPEAK, Permission.MUTE_MEMBERS, Permission.DEAFEN_MEMBERS, Permission.MOVE_MEMBERS));
  }
  return perms;
}

/** Highest role position a member holds. The owner outranks everyone. */
export function highestRolePosition(subject: Pick<PermissionSubject, 'userId' | 'ownerId' | 'roles' | 'memberRoleIds'>): number {
  if (subject.userId === subject.ownerId) return Number.POSITIVE_INFINITY;
  const held = new Set(subject.memberRoleIds);
  let top = 0;
  for (const role of subject.roles) {
    if (held.has(role.id) && role.position > top) top = role.position;
  }
  return top;
}

/** Whether `actor` sits strictly above `target` in the role hierarchy. */
export function outranks(
  actor: Pick<PermissionSubject, 'userId' | 'ownerId' | 'roles' | 'memberRoleIds'>,
  target: Pick<PermissionSubject, 'userId' | 'ownerId' | 'roles' | 'memberRoleIds'>,
): boolean {
  if (target.userId === target.ownerId) return false;
  if (actor.userId === actor.ownerId) return true;
  return highestRolePosition(actor) > highestRolePosition(target);
}

export function permissionNames(perms: number): PermissionName[] {
  return (Object.keys(Permission) as PermissionName[]).filter((name) => hasPermission(perms, Permission[name]));
}
